import { useCallback, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { makeStyles, tokens } from '@fluentui/react-components';
import { Home20Regular } from '@fluentui/react-icons';
import { Sidebar, type SidebarSection } from '@/components/Sidebar/Sidebar';
import { useTelemetry } from '@/hooks/useTelemetry';
import { tenantPrefix } from '@/routes';

const useStyles = makeStyles({
  body: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    minHeight: 0,
  },
  mainContent: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    paddingTop: tokens.spacingVerticalM,
    paddingRight: tokens.spacingHorizontalXXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
    paddingLeft: tokens.spacingHorizontalXXXL,
    background: `radial-gradient(ellipse 80% 60% at 70% 20%, color-mix(in srgb, ${tokens.colorBrandBackground2} 4%, transparent), transparent), radial-gradient(ellipse 60% 40% at 20% 80%, color-mix(in srgb, ${tokens.colorPaletteGreenBackground2} 3%, transparent), transparent)`,
  },
});

export const HomeLayout = () => {
  const styles = useStyles();
  const { trackEvent } = useTelemetry();
  const { locationName, tenantId: routeTenantId } = useParams<{ locationName?: string; tenantId?: string }>();

  const sections = useMemo<SidebarSection[]>(() => {
    // Use the route tenant param (not the store) to build the Home path so
    // NavLink matches the current URL. At the bare `/` route neither
    // routeTenantId nor locationName exist, and the path should be `/`.
    const prefix = tenantPrefix(routeTenantId);
    const homePath = locationName ? `${prefix}/location/${encodeURIComponent(locationName)}` : (prefix || '/');
    return [
      {
        id: 'home',
        items: [
          { id: 'home', label: 'Home', icon: Home20Regular, path: homePath, end: true },
        ],
      },
    ];
  }, [routeTenantId, locationName]);

  const handleSidebarItemClick = useCallback(
    (id: string) => trackEvent('navigation.sidebar.clicked', { destination: id }),
    [trackEvent],
  );

  return (
    <div className={styles.body}>
      <Sidebar
        ariaLabel="Home navigation"
        navAriaLabel="Home"
        sections={sections}
        onItemClick={handleSidebarItemClick}
      />
      <div className={styles.mainContent}>
        <Outlet />
      </div>
    </div>
  );
};
